/**
 * Legal Hub — top-level category hub for /legal.
 * Cross-references content/legal/*.md
 *
 * Lists 8 legal pages:
 *  /legal/privacy, /legal/terms, /legal/security, /legal/compliance,
 *  /legal/acceptable-use, /legal/cookies, /legal/data-processing, /legal/sla
 */

export const legalHubMeta = {
  title: "Legal & Compliance | Perception X2 by CryptoMize",
  description:
    "Privacy, terms, security, compliance, acceptable use, cookies, data processing, and SLA — every legal surface that governs the Perception X2 platform and its engagements.",
  canonical: "https://perception.ac/legal",
  slug: "legal",
};

export interface LegalChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const legalChildren: LegalChild[] = [
  {
    code: "01",
    title: "Privacy Policy",
    description:
      "How Perception X2 collects, processes, and protects personal information. GDPR, CCPA, and global privacy compliance.",
    href: "/legal/privacy",
    icon: "Lock",
    accent: "crimson",
  },
  {
    code: "02",
    title: "Terms of Service",
    description:
      "Master terms governing access to and use of the Perception X2 platform. Engagement scope, liability, and dispute resolution.",
    href: "/legal/terms",
    icon: "FileText",
    accent: "purple",
  },
  {
    code: "03",
    title: "Security",
    description:
      "Defense-grade security architecture: AES-256, TLS 1.3, zero trust, SOC 2 Type II, ISO 27001. Zero incidents in 15+ years.",
    href: "/legal/security",
    icon: "Shield",
    accent: "cyan",
  },
  {
    code: "04",
    title: "Compliance",
    description:
      "Regulatory compliance posture: SOC 2 Type II, ISO 27001, GDPR, CCPA, HIPAA-eligible, and defense-grade certifications.",
    href: "/legal/compliance",
    icon: "FileCheck",
    accent: "emerald",
  },
  {
    code: "05",
    title: "Acceptable Use",
    description:
      "Permitted and prohibited use of the Perception X2 platform. Ethical guardrails, brand safety, and regulatory boundaries.",
    href: "/legal/acceptable-use",
    icon: "CheckCircle",
    accent: "amber",
  },
  {
    code: "06",
    title: "Cookies Policy",
    description:
      "How Perception X2 uses cookies and similar technologies. Categorized, opt-in, and granular user controls.",
    href: "/legal/cookies",
    icon: "Cookie",
    accent: "denim",
  },
  {
    code: "07",
    title: "Data Processing",
    description:
      "Data Processing Addendum (DPA). GDPR Article 28 obligations, sub-processor disclosures, and cross-border transfer mechanisms.",
    href: "/legal/data-processing",
    icon: "Database",
    accent: "violet",
  },
  {
    code: "08",
    title: "Service Level Agreement",
    description:
      "Platform availability, response time, incident response, and remedy credits. Contractual SLA guarantees.",
    href: "/legal/sla",
    icon: "Award",
    accent: "rose",
  },
];

export interface LegalStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const legalStats: LegalStat[] = [
  { value: "8", label: "Legal Surfaces", to: 8, icon: "Scale", accent: "crimson" },
  { value: "0", label: "Security Incidents", to: 0, icon: "Shield", accent: "emerald" },
  { value: "99.9999%", label: "Uptime SLA", to: 99.9999, decimals: 4, suffix: "%", icon: "Activity", accent: "cyan" },
  { value: "6+", label: "Compliance Frameworks", to: 6, suffix: "+", icon: "FileCheck", accent: "amber" },
  { value: "GDPR", label: "Global Privacy", to: 100, suffix: "%", decimals: 0, icon: "Globe", accent: "purple" },
];

export interface LegalFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const legalFeatures: LegalFeature[] = [
  {
    title: "GDPR & CCPA Compliant",
    description: "Full compliance with EU GDPR, California CCPA, and global privacy regulations. Privacy by design, privacy by default.",
    icon: "Globe",
    accent: "crimson",
  },
  {
    title: "Defense-Grade Security",
    description: "AES-256 encryption, TLS 1.3, zero trust architecture, SOC 2 Type II, ISO 27001 — zero incidents in 15+ years.",
    icon: "Shield",
    accent: "emerald",
  },
  {
    title: "Audit-Grade Lineage",
    description: "Every metric, decision, and data point traceable to its source. Full lineage capture for compliance and regulatory review.",
    icon: "FileCheck",
    accent: "cyan",
  },
  {
    title: "Data Residency",
    description: "Multi-region active-active with sovereign-grade data residency. GDPR-compliant data processing in EU, US, APAC.",
    icon: "Map",
    accent: "purple",
  },
  {
    title: "Sub-Processor Disclosure",
    description: "Transparent sub-processor list, regular updates, and right to object. Full Article 28 GDPR compliance.",
    icon: "Users",
    accent: "amber",
  },
  {
    title: "Right to Erasure",
    description: "Full GDPR Article 17 right to erasure. Verified data deletion across all systems within 30 days.",
    icon: "Trash2",
    accent: "denim",
  },
  {
    title: "Cross-Border Transfers",
    description: "Standard Contractual Clauses, Binding Corporate Rules, and adequacy decisions. Compliant cross-border data flows.",
    icon: "ArrowLeftRight",
    accent: "violet",
  },
  {
    title: "Contractual SLA",
    description: "99.9999% uptime, sub-second stream latency, 24/7 incident response. Remedy credits on breach.",
    icon: "Award",
    accent: "rose",
  },
];

export interface LegalUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const legalUseCases: LegalUseCase[] = [
  {
    title: "GDPR Compliance Review",
    body: "Privacy by design verified against GDPR Article 25. Article 28 DPA in place. Cross-border transfer mechanisms documented and current.",
    icon: "Globe",
    accent: "crimson",
  },
  {
    title: "SOC 2 Type II Audit",
    body: "Annual SOC 2 Type II audit with continuous monitoring. Trust Service Criteria coverage across security, availability, and confidentiality.",
    icon: "FileCheck",
    accent: "emerald",
  },
  {
    title: "ISO 27001 Certification",
    body: "ISO/IEC 27001:2022 certified. Information Security Management System with documented controls and continuous improvement.",
    icon: "Award",
    accent: "cyan",
  },
  {
    title: "Defense & Sovereign Engagement",
    body: "Sovereign-grade data residency with cryptographic isolation. Air-gapped on-premise deployment available for classified environments.",
    icon: "Shield",
    accent: "purple",
  },
  {
    title: "Regulatory Reporting",
    body: "Audit-grade export for regulatory submissions. Full source lineage, timestamp preservation, and immutable evidence chain.",
    icon: "FileText",
    accent: "amber",
  },
  {
    title: "Vendor Risk Assessment",
    body: "Pre-completed security questionnaires (CAIQ, SIG, custom). On-demand attestation reports and penetration test summaries.",
    icon: "ClipboardCheck",
    accent: "denim",
  },
];

export interface LegalWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const legalWhy: LegalWhy[] = [
  {
    title: "Zero Security Incidents",
    body: "15+ years of operations without a single security breach. The same standards used by defense and intelligence agencies.",
    metric: "0",
    accent: "crimson",
  },
  {
    title: "SOC 2 Type II",
    body: "Annual SOC 2 Type II audit with continuous monitoring. All five Trust Service Criteria covered with documented controls.",
    metric: "✓",
    accent: "emerald",
  },
  {
    title: "ISO 27001 Certified",
    body: "ISO/IEC 27001:2022 certified Information Security Management System. Documented controls and continuous improvement.",
    metric: "✓",
    accent: "cyan",
  },
  {
    title: "GDPR Compliant",
    body: "Full Article 28 DPA, Article 17 right to erasure, Article 30 records of processing, Article 32 security of processing.",
    metric: "✓",
    accent: "purple",
  },
  {
    title: "99.9999% Uptime",
    body: "Contractual SLA with remedy credits. Multi-region active-active with sub-millisecond failover.",
    metric: "99.9999%",
    accent: "amber",
  },
  {
    title: "Sovereign-Grade Residency",
    body: "EU, US, APAC data residency options. Air-gapped on-premise deployment for defense and sovereign clients.",
    metric: "✓",
    accent: "denim",
  },
];

export interface LegalFaq {
  q: string;
  a: string;
}

export const legalFaq: LegalFaq[] = [
  {
    q: "What are the eight Perception X2 legal surfaces?",
    a: "The eight legal surfaces are: Privacy Policy, Terms of Service, Security, Compliance, Acceptable Use, Cookies Policy, Data Processing, and Service Level Agreement. Each addresses a distinct legal or compliance surface that governs engagement with the Perception X2 platform.",
  },
  {
    q: "Is Perception X2 GDPR compliant?",
    a: "Yes. Perception X2 is fully GDPR compliant. We have a Data Processing Addendum (Article 28), support the right to erasure (Article 17), maintain records of processing (Article 30), implement security of processing (Article 32), and use Standard Contractual Clauses for cross-border transfers.",
  },
  {
    q: "What security certifications does Perception X2 hold?",
    a: "Perception X2 holds SOC 2 Type II, ISO/IEC 27001:2022, and is GDPR, CCPA, and HIPAA-eligible. The platform uses AES-256 encryption, TLS 1.3, and zero trust architecture. We have zero security incidents in 15+ years of operation.",
  },
  {
    q: "How is data residency handled?",
    a: "Perception X2 supports EU, US, and APAC data residency with multi-region active-active deployment. Sovereign clients can opt for cryptographic isolation per engagement. Air-gapped on-premise deployment is available for defense and classified environments.",
  },
  {
    q: "What is the contractual SLA?",
    a: "Perception X2's contractual SLA guarantees 99.9999% uptime (31.5s downtime per year), sub-second stream latency, sub-200ms API response at p99, and 24/7 incident response. Remedy credits are issued on SLA breach.",
  },
  {
    q: "Does Perception X2 support HIPAA?",
    a: "Yes. Perception X2 is HIPAA-eligible. For healthcare clients, we sign a Business Associate Agreement (BAA) and implement additional technical safeguards as required by the HIPAA Security Rule.",
  },
  {
    q: "How does Perception X2 handle data subject access requests?",
    a: "Data subject access requests are processed within 30 days, with full data export in machine-readable format. Right to erasure (Article 17 GDPR) is implemented with verified deletion across all systems, including backups within their retention window.",
  },
  {
    q: "Where can I find the sub-processor list?",
    a: "The current sub-processor list is published in the Data Processing page (/legal/data-processing). We provide at least 30 days' notice of sub-processor changes, and clients retain the right to object.",
  },
  {
    q: "What is the Acceptable Use Policy?",
    a: "The Acceptable Use Policy defines permitted and prohibited use of the Perception X2 platform. We do not support use cases that violate international sanctions, target vulnerable populations, promote violence, or facilitate illegal activity. Brand-safety guardrails are configurable per engagement.",
  },
  {
    q: "How are cookies used on Perception X2 properties?",
    a: "Cookies are used for essential platform functionality, analytics, and user preferences. We use categorized, opt-in cookies with granular user controls. No advertising or third-party tracking cookies are set without explicit consent.",
  },
];

export const legalCta = {
  eyebrow: "Engage with Confidence",
  title: "Eight surfaces. Defense-grade trust.",
  description:
    "Every Perception X2 engagement is governed by clear legal, security, and compliance terms. The eight legal surfaces ensure transparency, auditability, and protection for both client and platform.",
  primary: { label: "Contact Legal Team", href: "/contact" },
  secondary: { label: "View Security", href: "/legal/security" },
  trustItems: [
    "8 legal surfaces",
    "SOC 2 Type II",
    "ISO 27001",
    "GDPR compliant",
    "Zero incidents",
  ],
};

/* Compliance frameworks — bespoke visual data */
export interface ComplianceFramework {
  name: string;
  coverage: number;
  status: "Certified" | "Compliant" | "Eligible" | "In Progress";
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const complianceFrameworks: ComplianceFramework[] = [
  { name: "SOC 2 Type II", coverage: 100, status: "Certified", accent: "emerald" },
  { name: "ISO 27001:2022", coverage: 100, status: "Certified", accent: "cyan" },
  { name: "GDPR (EU)", coverage: 100, status: "Compliant", accent: "crimson" },
  { name: "CCPA (California)", coverage: 100, status: "Compliant", accent: "purple" },
  { name: "HIPAA (US)", coverage: 100, status: "Eligible", accent: "amber" },
  { name: "PCI-DSS", coverage: 90, status: "In Progress", accent: "denim" },
  { name: "FedRAMP Moderate", coverage: 85, status: "In Progress", accent: "violet" },
  { name: "Defense-grade", coverage: 100, status: "Certified", accent: "rose" },
];

/* SLA response tiers — bespoke visual data */
export interface SlaTier {
  tier: string;
  description: string;
  response: string;
  resolution: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const slaTiers: SlaTier[] = [
  {
    tier: "P0 · Critical",
    description: "Platform-wide outage or material security incident.",
    response: "< 5 min",
    resolution: "< 60 min",
    accent: "crimson",
  },
  {
    tier: "P1 · High",
    description: "Material feature degradation or limited outage.",
    response: "< 15 min",
    resolution: "< 4 hr",
    accent: "amber",
  },
  {
    tier: "P2 · Standard",
    description: "Standard functional issue with available workaround.",
    response: "< 1 hr",
    resolution: "< 24 hr",
    accent: "cyan",
  },
  {
    tier: "P3 · Low",
    description: "Cosmetic issue or minor feature request.",
    response: "< 8 hr",
    resolution: "< 5 d",
    accent: "emerald",
  },
];
